import React, { useCallback, useEffect, useMemo } from 'react'

import analytics from 'global/AnalyticsData'

import SideBySideVideoPage from './SideBySideVideoPage'

const DEFAULT_RECORD_OR = 'OR'

function stepToConfig(step) {
  // CMS may nest sideBySideVideo inside gen_ai_tips until step type is migrated
  const sbv =
    step.sideBySideVideo || {}

  const layout = sbv.layout === 'STACKED' ? 'stacked' : 'side-by-side'

  const bgColor = sbv.bgColor || '#051a61'
  const brandColor = sbv.brandColor || '#2659e5'
  const textColor = sbv.textColor || '#ffffff'
  // bgSecondary, bgAccent, brandPrimaryDark not in CMS — derive from the 3 inputs
  const bgSecondary = bgColor // same bg, components can layer opacity if needed
  const bgAccent = '#e0edff' // fixed light accent (not theme-critical, used for splash bg)
  const brandPrimaryDark = brandColor // no separate dark variant from CMS

  const baseVideos = (sbv.baseVideos || []).map(v => ({
    id: v.id,
    title: v.title || '',
    description: v.description || '',
    src: v.videoId || '',
    thumbnailSrc: v.thumbnailId || undefined,
    durationSeconds: v.durationSeconds || 15,
  }))

  return {
    id: step.id || 'sbv',
    name: sbv.splashTitle || 'Side by Side Video',
    layout,
    theme: {
      bgPrimary: bgColor,
      bgSecondary: bgSecondary,
      bgAccent: bgAccent,
      brandPrimary: brandColor,
      brandPrimaryDark: brandPrimaryDark,
      textOnDark: textColor,
      textOnDarkMuted: textColor + 'cc', // 80% opacity of text color as muted variant
      textOnLight: '#1a1a1a',
      textOnLightMuted: '#666666',
      fontHeading: "'Nunito', sans-serif",
      fontBody: "'Inter', sans-serif",
      radiusCard: 24,
      radiusButton: 12,
    },
    copy: {
      splashTitle: sbv.splashTitle || '',
      splashSubtitle: sbv.splashSubtitle || '',
      splashCta: sbv.splashCtaText || 'Get Started',
      selectionTitle: sbv.selectTitle || 'Choose Your Video',
      selectionSubtitle: sbv.selectSubtitle || '',
      selectionCta: sbv.selectCtaText || 'Next →',
      recordTitle: sbv.recordTitle || 'Record Your Video',
      recordSubtitle: sbv.recordSubtitle || '',
      recordAddYourVideo: 'Add your video',
      recordCtaRecord: sbv.recordCtaRecord || 'Record a video',
      recordCtaUpload: sbv.recordCtaUpload || 'Upload from Gallery',
      recordOr: DEFAULT_RECORD_OR,
      previewTitle: sbv.previewTitle || 'Preview',
      previewSubtitle: sbv.previewSubtitle || '',
      previewCta: sbv.previewCtaText || 'Save & Continue →',
      previewCheckboxes: (sbv.previewCheckboxes || []).map(c => ({
        id: c.id,
        label: c.label || '',
        required: !!c.required,
      })),
      previewRerecordText: sbv.previewRerecordText || 'Re-record Video',
      downloadTitle: sbv.downloadTitle || 'Your Video is Ready!',
      downloadSubtitle: sbv.downloadSubtitle || '',
      downloadCta: sbv.downloadCtaText || 'Download',
      shareTitle: sbv.shareTitle || undefined,
      shareText: sbv.shareText || undefined,
      shareUrl: sbv.shareUrl || undefined,
    },
    branding: {
      footer: sbv.footerText || '',
      splashImageSrc: sbv.splashImageId || undefined,
      splashMobileImageSrc:
        sbv.splashMobileImageId || sbv.splashImageId || undefined,
      overlayImageSrc: sbv.overlayImageId || undefined,
    },
    baseVideos,
    maxRecordSeconds: 60,
  }
}

const SideBySideVideoStep = ({
  step,
  onEngage,
  onAwardPoints,
  onChangeBackground,
  nextStepButton,
}) => {
  const config = useMemo(() => stepToConfig(step), [step])

  // Paint the whole step panel with our background so the template's structural
  // footer strip (rendered below our root, inside the panel) blends seamlessly
  // instead of showing the template's default background.
  // Deps intentionally exclude onChangeBackground — the parent passes a fresh
  // function ref each render, which would otherwise loop (effect → setState →
  // re-render → effect).
  useEffect(() => {
    onChangeBackground &&
      onChangeBackground({ backgroundColor: config.theme.bgPrimary })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.theme.bgPrimary])

  // Step is "complete" once the video is generated (or render failed) — mark it
  // engaged so the break reveals the next-step arrow, even if the user never
  // downloads. Points are still awarded on the download action below.
  const handleReady = () => {
    onEngage && onEngage()
  }

  const handleDownloaded = () => {
    onEngage && onEngage()
    onAwardPoints && onAwardPoints(step.possibleCoinsAwarded || 0)
  }

  // BR engagement metrics — fires step_interaction_sbsv_* events to BigQuery
  // for completion-rate / fall-off analysis.
  const track = useCallback(
    (eventName, data = {}) => {
      analytics.stepEvent(eventName, step, data)
    },
    [step],
  )

  return (
    <SideBySideVideoPage
      config={config}
      showEnginePicker={false}
      onDownloaded={handleDownloaded}
      onReady={handleReady}
      nextStepButton={nextStepButton}
      onEvent={track}
    />
  )
}

export default SideBySideVideoStep
